/**
 * Script / CSS loader with a module-level promise cache.
 * Guarantees that concurrent callers waiting on the same URL
 * all share a single in-flight promise instead of resolving early.
 */
const scriptCache = new Map<string, Promise<void>>();

export function loadScript(src: string): Promise<void> {
  /* Return cached promise (covers both in-flight and already-done) */
  if (scriptCache.has(src)) return scriptCache.get(src)!;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (existing) {
      /* Tag is in DOM — attach listener in case it's still loading */
      existing.addEventListener('load',  () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load: ${src}`)), { once: true });
      /* If it already executed (readyState trick isn't reliable cross-browser,
         so also check if the global was already populated at call-site) */
      if ((existing as HTMLScriptElement & { readyState?: string }).readyState === 'complete') {
        resolve();
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load',  () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load: ${src}`)), { once: true });
    document.head.appendChild(script);
  });

  scriptCache.set(src, promise);
  return promise;
}

export function loadCSS(href: string): void {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}
